import { useState, useEffect } from 'react';
import { MediaItem } from '../types';
import { fetchMediaJsonFromS3 } from '../components/VideoEditor/mediaItemService';
import { handleSplitMediaItem } from '../components/VideoEditor/mediaSplitHandler';
import { reloadMediaItemsFromDB } from '../components/VideoEditor/mediaReloadHandler';
import { filterVideoSegments } from '../utils/videoSegmentUtils';

interface UseMediaItemsProps {
  projectId: string;
  audioUrl: string | null;
  videoUrl: string | null;
  isVideoSegmented: boolean;
  showNotification: (notification: any) => void;
  detectAspectRatio?: (items: MediaItem[]) => void;
  onLoadComplete?: () => void;
}

export const useMediaItems = ({
  projectId,
  audioUrl,
  videoUrl,
  isVideoSegmented,
  showNotification,
  detectAspectRatio,
  onLoadComplete
}: UseMediaItemsProps) => {
  const [mediaItems, setMediaItems] = useState<MediaItem[]>([]);
  const [initialMediaItems, setInitialMediaItems] = useState<MediaItem[]>([]);
  const [allMediaItemsFromDB, setAllMediaItemsFromDB] = useState<MediaItem[]>([]);
  const [selectedMediaItemId, setSelectedMediaItemId] = useState<string | null>(null);
  const [videoSegments, setVideoSegments] = useState<MediaItem[]>([]);
  const [isTimelineLoading, setIsTimelineLoading] = useState(true);

  // Load media JSON once projectId & URL exist
  useEffect(() => {
    console.log('🔍 [TIMELINE DEBUG] Media load effect triggered:', {
      projectId,
      audioUrl,
      videoUrl,
      isTimelineLoading
    });
    
    const hasMediaSource = audioUrl || videoUrl;
    if (!projectId || !hasMediaSource) {
      console.log('⚠️ [TIMELINE DEBUG] Early return - clearing loading state');
      setIsTimelineLoading(false);
      return;
    }

    console.log('✅ [TIMELINE DEBUG] Starting media load...');

    fetchMediaJsonFromS3(projectId)
      .then((response) => {
        const data = response as MediaItem[];
        
        // Check if we got valid data
        if (!data || !Array.isArray(data)) {
          console.error('❌ [MEDIA LOAD] Invalid data structure received:', data);
          showNotification({
            type: "error",
            message: "Invalid media data structure",
            title: "Error"
          });
          return;
        }
        
        if (data?.length) {
          console.log('📦 [MEDIA LOAD] Total items from DB:', data.length);
          
          const previewSegments = data.filter(item => 
            item.effect?.includes('preview-segment')
          );
          console.log('📦 [MEDIA LOAD] Preview segments found:', previewSegments.length);
          
          if (previewSegments.length > 0) {
            console.log('📦 [MEDIA LOAD] First preview segment:', {
              name: previewSegments[0].name,
              effect: previewSegments[0].effect,
              start: previewSegments[0].start,
              end: previewSegments[0].end
            });
          }

          // Save ALL data (including preview segments) for later use
          setAllMediaItemsFromDB(data);

          // Filter out archived AND preview segments on initial load
          const activeData = data.filter(item => 
            item?.effect !== 'archived' && 
            !item?.effect?.startsWith('preview-segment')
          );
          
          console.log('📦 [MEDIA LOAD] Active items (no previews):', activeData.length);
          
          // Save initial state for preview reset
          setInitialMediaItems(activeData);
          
          // For segmented videos, add temporary "isLoaded" field
          const loadedMedia = activeData.map((mediaItem) => ({
            ...mediaItem,
            id: `${Date.now()}-${Math.floor(Math.random() * 10000)}-${mediaItem.name}`,
            isLoaded: isVideoSegmented && mediaItem.type === 'video' ? false : true,
          }));

          setMediaItems(loadedMedia);

          if (detectAspectRatio) {
            console.log("🎯 [PROJECT LOAD] Detecting aspect ratio for project");
            // Delay detection to ensure media is fully processed
            setTimeout(() => {
              detectAspectRatio(loadedMedia);
            }, 500);
          }

          if (isVideoSegmented) {
            const videoCount = data.filter(item => item.type === 'video').length;
            console.log(`🎬 Loaded ${videoCount} video segments (appearing as dark until clicked)`);
          }

          // showNotification({
          //   type: "success",
          //   message: "Successfully loaded medias!",
          //   title: "Success"
          // });
        } else {
          // No media items found - this is valid, just log it
          console.log('📦 [MEDIA LOAD] No media items found for project');
          setMediaItems([]);
          setInitialMediaItems([]);
          setAllMediaItemsFromDB([]);
        }
      })
      .catch((error: Error) => {
        console.error("Error fetching media:", error);
        showNotification({
          type: "error",
          message: `Failed to load media! ${error.message}`,
          title: "Error"
        });
      })
      .finally(() => {
        console.log('✅ [TIMELINE DEBUG] Media load complete - clearing loading state');
        setIsTimelineLoading(false);
        if (onLoadComplete) onLoadComplete();
      });
  }, [projectId, audioUrl, videoUrl]);

  // Filter video segments when mediaItems change
  useEffect(() => {
    if (!isVideoSegmented || mediaItems.length === 0) return;
    
    const segments = filterVideoSegments(mediaItems);
    setVideoSegments(segments);
    
    console.log(`📦 Filtered ${segments.length} video segments:`, 
      segments.map(s => ({ name: s.name, start: s.start, end: s.end }))
    );
  }, [mediaItems, isVideoSegmented]);

  // Split media item
  const splitMediaItem = (itemId: string, splitTime?: number, currentTime?: number) => {
    handleSplitMediaItem(
      itemId,
      splitTime,
      currentTime || 0,
      mediaItems,
      setMediaItems,
      setSelectedMediaItemId,
      showNotification
    );
  };

  // Reload media items from DB
  const reloadMediaItems = () => {
    reloadMediaItemsFromDB(projectId, {
      setAllMediaItemsFromDB,
      setInitialMediaItems,
      showNotification
    });
  };

  return {
    mediaItems,
    setMediaItems,
    initialMediaItems,
    setInitialMediaItems,
    allMediaItemsFromDB,
    setAllMediaItemsFromDB,
    selectedMediaItemId,
    setSelectedMediaItemId,
    videoSegments,
    isTimelineLoading,
    setIsTimelineLoading,
    splitMediaItem,
    reloadMediaItems
  };
};
